/**
 * Centralized user-based visibility and restriction checks.
 * Add new user IDs or checks here so they stay in one place.
 *
 * USAGE:
 * ------
 * 1. In any component that receives `user` from Redux/props:
 *
 *    import { shouldHideSupplyChains, hasRestrictedGroupForDeferralMenu } from "constants/user-visibility";
 *
 *    // Hide something when user is in the supply-chains hide list:
 *    { shouldHideSupplyChains(this.props.user) ? null : <SupplyChainsTable /> }
 *
 *    // Hide menu items when user has restricted groups (7, 11, 16):
 *    if (hasRestrictedGroupForDeferralMenu(this.props.user)) { ... }
 *
 * 2. To add a new check: add a new array (e.g. SOME_FEATURE_USER_IDS) and a
 *    helper (e.g. shouldHideSomeFeature(user)) that uses getUserId(user), then
 *    use that helper in your component.
 *
 * JSDoc (the @param and @returns lines) are optional documentation:
 *   @param {Object} user - describes the function's argument
 *   @returns {boolean} - describes what the function returns
 * They help IDEs show tooltips and improve autocomplete; they are not required to run.
 */

/**
 * Get current user id from user object (supports Immutable and plain object).
 * @param {Object} user - User object from props (may be Immutable Map or plain object)
 * @returns {number|null} User id or null
 */
export const getUserId = (user) => {
    if (!user) return null;
    // If user has getIn (Immutable Map): try getIn(["data","id"]) then get("id"). Else (plain object): use user.id.
    // We never call user.getIn() when user.getIn is undefined — the ternary picks the other branch.
    const id = user.getIn
        ? (user.getIn(["data", "id"]) ?? user.get("id"))
        : user.id;
    return id != null ? Number(id) : null;
};

/**
 * Get current user email from user object (supports Immutable and plain object).
 * @param {Object} user - User object from props (may be Immutable Map or plain object)
 * @returns {string|null} User email (lowercased) or null
 */
export const getUserEmail = (user) => {
    if (!user) return null;
    const email = user.getIn
        ? (user.getIn(["data", "email"]) ?? user.get("email"))
        : user.email;
    return email ? String(email).toLowerCase() : null;
};

/**
 * Get the current user's numeric level_of_authority (supports Immutable and plain object).
 * Returns null when the user has no LOA. A value of 0 is treated as "no LOA"
 * by the callers below (0 or null => fall back to the existing permission check).
 * @param {Object} user - User object from props (may be Immutable Map or plain object)
 * @returns {number|null}
 */
export const getLevelOfAuthority = (user) => {
    if (!user) return null;
    const loa = user.getIn
        ? (user.getIn(["data", "level_of_authority"]) ?? user.get("level_of_authority"))
        : user.level_of_authority;
    return loa != null ? Number(loa) : null;
};

/**
 * Get the current user's level_of_authority_label (e.g. "L1", "L2").
 * @param {Object} user - User object from props (may be Immutable Map or plain object)
 * @returns {string|null}
 */
export const getLevelOfAuthorityLabel = (user) => {
    if (!user) return null;
    const label = user.getIn
        ? (user.getIn(["data", "level_of_authority_label"]) ?? user.get("level_of_authority_label"))
        : user.level_of_authority_label;
    return label != null ? String(label) : null;
};

/**
 * Whether the user is governed by the level-of-authority (LOA) gating.
 * True only when level_of_authority is a non-null, non-zero number.
 * When false, callers must fall back to the existing permission check.
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const hasLevelOfAuthority = (user) => {
    const loa = getLevelOfAuthority(user);
    return loa != null && loa !== 0;
};

/**
 * Whether the user may download the Initial Screening report (Company Screening
 * card in Credit Checker).
 *
 *   1. level_of_authority_label L1 / L2 / L3  -> allowed outright.
 *   2. Otherwise — L0, or no level_of_authority (null / 0) — fall back to the
 *      stage gate: allowed only on stage 7, 8, or 11.
 *   3. Anything else (e.g. an LOA label outside L1–L3 that isn't L0) -> blocked.
 *
 * @param {Object} user - User object from props
 * @param {number|string} stageId - current case stage
 * @returns {boolean}
 */
export const canDownloadInitialScreeningReport = (user, stageId) => {
    const label = getLevelOfAuthorityLabel(user);
    if (["L1", "L2", "L3"].includes(label)) {
        return true;
    }
    if (!hasLevelOfAuthority(user) || label === "L0") {
        return [7, 8, 11].includes(Number(stageId));
    }
    return false;
};

/**
 * Permission groups always allowed to open company verification documents —
 * Credit Manager (7) and Risk Approver (8).
 */
export const COMPANY_DOC_VIEW_ALLOWED_GROUP_IDS = [7, 8];

/**
 * Permission groups blocked from opening company verification documents —
 * Credit Administrator (11). Overridden by the LOA labels and by
 * COMPANY_DOC_VIEW_ALLOWED_GROUP_IDS above.
 */
export const COMPANY_DOC_VIEW_BLOCKED_GROUP_IDS = [11];

/**
 * Whether the user may view a company verification document (GST
 * Certificate, Utility Bill, Recommendation Letter, Takeover Document) in
 * Credit Checker's Company Detail section. Checks, in order:
 *
 *   - level_of_authority_label L1 / L2 / L3 / L4 -> allowed outright,
 *     regardless of userGroups.
 *   - permission group Credit Manager (7) or Risk Approver (8) -> allowed,
 *     even when the user also sits in a blocked group.
 *   - otherwise blocked only when the user is in Credit Administrator (11).
 *
 * @param {Object} user - User object from props
 * @param {Array} userGroups - Logged-in user's permission group ids (e.g. [7, 11])
 * @returns {boolean}
 */
export const canViewCompanyVerificationDoc = (user, userGroups) => {
    const label = getLevelOfAuthorityLabel(user);
    if (["L1", "L2", "L3", "L4"].includes(label)) {
        return true;
    }
    if (!Array.isArray(userGroups)) return true;
    const groupIds = userGroups.map(Number);
    if (groupIds.some(id => COMPANY_DOC_VIEW_ALLOWED_GROUP_IDS.includes(id))) {
        return true;
    }
    return !groupIds.some(id => COMPANY_DOC_VIEW_BLOCKED_GROUP_IDS.includes(id));
};

/**
 * Map of case-details tab name -> list of LOA labels allowed to see that tab.
 * ONLY consulted for users who have a level_of_authority set (see
 * hasLevelOfAuthority). For everyone else the existing per-tab `permission`
 * check applies unchanged.
 *
 * A tab that is missing from this map, or whose label list does not include the
 * user's label, is HIDDEN for LOA users. Fill in the real label lists per tab.
 */
export const TAB_AUTHORITY_LABELS = {
    "Adhoc/Enhancement": ["L1", "L2", "L3", "L4"],
    "Credit Summary": ["L1", "L2", "L3", "L4"],
    "Credit Checker": ["L0", "L1", "L2", "L3", "L4"],
    "Credit Brief": ["L0", "L1", "L2", "L3", "L4"],
    "Financial Analysis": ["L1", "L2", "L3", "L4"],
    "Doc Checklist": ["L1", "L2", "L3", "L4"],
    "FI / Bank BRE": ["L1", "L2", "L3", "L4"],
    "CAM View": ["L1", "L2", "L3", "L4"],
    "All Cases": ["L1", "L2", "L3", "L4"],
    "Documents Review": ["L0", "L1", "L2", "L3", "L4"],
    "Ops Workspace": ["L0", "L1", "L2", "L3", "L4"],
    "Case Details": ["L1", "L2", "L3", "L4"],
    "Bureau Details": ["L1", "L2", "L3", "L4"],
    "Documents": ["L1", "L2", "L3", "L4"],
    "MIS": ["L1", "L2", "L3", "L4"],
    "Post Sanction Documents": ["L1", "L2", "L3", "L4"],
    "Credit Appraisal": ["L1", "L2", "L3", "L4"],
    "NACH Mandate": ["L1", "L2", "L3", "L4"],
    "Score Card": ["L1", "L2", "L3", "L4"],
    "Account/Facility Details": ["L1", "L2", "L3", "L4"],
    "PD Questionnaire": ["L1", "L2", "L3", "L4"],
    "Account View": ["L1", "L2", "L3", "L4"],
    "Agreement": ["L1", "L2", "L3", "L4"],
    "Risk Controls": ["L1", "L2", "L3", "L4"],
    "Cam View (New)": ["L1", "L2", "L3", "L4"],
    "Seller View": ["L0", "L1", "L2", "L3", "L4"],
};

/**
 * Whether a given tab is allowed by the user's LOA label.
 * @param {Object} user - User object from props
 * @param {string} tabName - The tab's display name
 * @returns {boolean}
 */
export const isTabAllowedByAuthorityLabel = (user, tabName) => {
    const label = getLevelOfAuthorityLabel(user);
    const allowed = TAB_AUTHORITY_LABELS[tabName];
    return !!label && Array.isArray(allowed) && allowed.includes(label);
};

/**
 * Generic LOA gate for any UI section (not just tabs).
 *
 *   - If the user has NO level_of_authority (null/0): returns true
 *     (existing behaviour — section shown as before).
 *   - If the user HAS a level_of_authority: returns true only when their
 *     level_of_authority_label is included in `allowedLabels`.
 *
 * @param {Object} user - User object from props
 * @param {string[]} allowedLabels - LOA labels allowed to see the section
 * @returns {boolean}
 */
export const isSectionVisibleByAuthority = (user, allowedLabels) => {
    if (!hasLevelOfAuthority(user)) return true;
    const label = getLevelOfAuthorityLabel(user);
    return !!label && Array.isArray(allowedLabels) && allowedLabels.includes(label);
};

/**
 * Resolve whether a case-details tab is visible for the current user.
 *
 *   - If the user has a level_of_authority (non-null, non-zero): use the LOA
 *     label map (isTabAllowedByAuthorityLabel) and ignore the tab permission.
 *   - Otherwise: fall back to the existing permission check
 *     (no permission required, or context.get(permission) truthy).
 *
 * @param {Object} user - User object from props
 * @param {Object} tab - Tab config ({ tabName, permission })
 * @param {Object} context - Permission context (Immutable Map with .get)
 * @returns {boolean}
 */
export const isTabVisible = (user, tab, context) => {
    if (hasLevelOfAuthority(user)) {
        return isTabAllowedByAuthorityLabel(user, tab.tabName);
    }
    return !tab.permission || (!!context && !!context.get(tab.permission));
};

/**
 * Case-details tab ordering overrides, keyed by level_of_authority_label.
 * Tabs named here are pinned to the front of the tab strip in this order;
 * every other visible tab keeps following the existing default sequence.
 * Labels not listed here (and users without a level_of_authority) get no
 * override — existing tab order is unchanged.
 */
export const CASE_DETAILS_TAB_PRIORITY_ORDER = {
    L0: ["Credit Checker", "Credit Brief", "Documents Review"],
    L1: ["Credit Brief", "Credit Checker"],
    L2: ["Credit Brief", "Credit Checker"],
    L3: ["Credit Brief", "Credit Checker"],
    L4: ["Credit Brief", "Credit Checker"],
};

/**
 * Resolve the tab-name priority order for the current user, based on their
 * level_of_authority_label. Returns [] when the user has no LOA (existing
 * tab order applies unchanged).
 * @param {Object} user - User object from props
 * @returns {string[]}
 */
export const getCaseDetailsTabPriorityOrder = (user) => {
    if (!hasLevelOfAuthority(user)) return [];
    const label = getLevelOfAuthorityLabel(user);
    return CASE_DETAILS_TAB_PRIORITY_ORDER[label] || [];
};

/**
 * Default case-details landing tab (URL slug) overrides, keyed by
 * level_of_authority_label. Mirrors the front of CASE_DETAILS_TAB_PRIORITY_ORDER
 * above — the tab a user is dropped into first should be the tab that's
 * pinned first for them.
 */
export const CASE_DETAILS_DEFAULT_LANDING_TAB_BY_LABEL = {
    L0: "credit_checker",
    L1: "credit_brief",
    L2: "credit_brief",
    L3: "credit_brief",
    L4: "credit_brief",
};

/**
 * Resolve the URL slug of the tab a case should open on for the current user.
 * Users without a level_of_authority (or a label outside the map) keep
 * getting `fallback` — the existing default landing tab.
 * @param {Object} user - User object from props/store
 * @param {string} fallback - Existing default landing tab slug (e.g. "credit_summary")
 * @returns {string}
 */
export const getDefaultCaseLandingTab = (user, fallback = "credit_summary") => {
    if (!hasLevelOfAuthority(user)) return fallback;
    const label = getLevelOfAuthorityLabel(user);
    return CASE_DETAILS_DEFAULT_LANDING_TAB_BY_LABEL[label] || fallback;
};

/**
 * Whether to hide the Product Details / Contact Points / Miscellaneous / Actionable
 * cards on the Case Details base (summary) page.
 *
 *   - No level_of_authority (null/0): false — cards shown as before.
 *   - level_of_authority_label "L0": false — cards shown as before.
 *   - level_of_authority_label "L1"-"L4" (or any other non-L0 label): true — hidden.
 *
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const shouldHideCaseSummaryBaseCards = (user) => {
    if (!hasLevelOfAuthority(user)) return false;
    return getLevelOfAuthorityLabel(user) !== "L0";
};

/**
 * User IDs for whom the Supply Chains table (Account Facility) is hidden.
 * Add or remove IDs here for future changes.
 */
export const HIDE_SUPPLY_CHAINS_USER_IDS = [
    183438,185921
];

/**
 * Whether to hide the Supply Chains table for the given user.
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const shouldHideSupplyChains = (user) => {
    const userId = getUserId(user);
    return userId != null && HIDE_SUPPLY_CHAINS_USER_IDS.includes(userId);
};

/**
 * User IDs allowed to edit the "Bundled" toggle on the case details summary.
 * For everyone else the toggle stays disabled (view only).
 */
export const BUNDLED_TAG_EDIT_USER_IDS = [
    346, 371023, 197048
];

/**
 * Whether the given user may edit the "Bundled" toggle.
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const canEditBundledTag = (user) => {
    const userId = getUserId(user);
    return userId != null && BUNDLED_TAG_EDIT_USER_IDS.includes(userId);
};

/**
 * Group IDs (7, 11, 16) - users in these groups get db_onboarding and anchor_onboarding
 * removed from header menu. Used in header-menu.js
 */
export const RESTRICTED_GROUP_IDS_FOR_DEFERRAL_MENU = [7, 11, 16];

/**
 * Emails explicitly allowed to access db_onboarding / anchor_onboarding, even if
 * they belong to a restricted group. Acts as an allowlist override.
 */
export const DEFERRAL_MENU_ALLOWED_EMAILS = [
    "ajay.nagpal@progfin.com",
    "sakshi.chaudhary@progfin.com",
    "esha.kad@progfin.in",
];

/**
 * Whether the user has a restricted group (for hiding db_onboarding / anchor_onboarding in header).
 * Users whose email is in DEFERRAL_MENU_ALLOWED_EMAILS are never treated as restricted.
 * @param {Object} user - User object from props (with get("groups"))
 * @returns {boolean}
 */
export const hasRestrictedGroupForDeferralMenu = (user) => {
    const email = getUserEmail(user);
    if (email && DEFERRAL_MENU_ALLOWED_EMAILS.includes(email)) return false;
    const groups = user?.getIn?.(["data", "groups"]) ?? user?.get?.("groups");
    if (!groups || !groups.some) return false;
    return groups.some((group) => {
        const groupId = group?.id ?? group?.get?.("id");
        return RESTRICTED_GROUP_IDS_FOR_DEFERRAL_MENU.includes(Number(groupId));
    });
};

/**
 * Groups allowed to see the "Schedule a Call" card (Teams call scheduling) —
 * Credit Manager (7). Add or remove group ids here for future changes.
 */
export const CALL_SCHEDULE_ALLOWED_GROUP_IDS = [7];

/**
 * Whether the given user may see the Schedule-a-Call card: anyone in a
 * CALL_SCHEDULE_ALLOWED_GROUP_IDS group.
 * @param {Object} user - User object from props
 * @returns {boolean}
 */
export const canViewCallSchedule = (user) => {
    const groups = user?.getIn?.(["data", "groups"]) ?? user?.get?.("groups");
    if (!groups || !groups.some) return false;
    return groups.some((group) => {
        const groupId = group?.id ?? group?.get?.("id");
        return CALL_SCHEDULE_ALLOWED_GROUP_IDS.includes(Number(groupId));
    });
};
